import React, { useState, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';

import { Button } from 'ui-components/components/button';
import { Checkbox } from 'ui-components/components/checkbox';
import { FlexColumn } from 'ui-components/components/flex';
import { INLINE_MESSAGE_TYPES } from 'ui-components/components/inline-message/types';
import { Input, version2 } from 'ui-components/components/input';
import { Text, TextType } from 'ui-components/components/text';
import { Tooltip } from 'ui-components/components/tooltip';
import { TooltipContent } from 'ui-components/components/tooltip/tooltip-content';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import { CountryConfigNames } from '../../../../../../contexts/country-config-context/country-config-enums';
import { UserSettings } from '../../../../../../enums/user-settings/user-settings';
import { useCountryConfig } from '../../../../../../hooks/use-country-config';
import { useCurrentUser } from '../../../../../../hooks/use-current-user';
import { useWeeklyDigestSetting } from '../../../../../../hooks/use-user-setting';
import { sanitizeEmailAddress } from '../../../../../../services/email-sanitation/sanitizeEmailAddress';
import { LogService } from '../../../../../../services/log-service';
import Global from '../../../../../../store/global-selectors';
import { getCurrentUserSuccess, updateUsersList, updateUserSetting } from '../../../../../../store/user/user.actions';
import { updateProfile } from '../../../../api/my-profile';
import messages from '../../../messages';
import style from '../../../style.module.less';
import { getDefaultProfileErrorState, validateProfile } from '../helpers';
import { showErrorNotification, showSuccessNotification } from '../notifications';

// TODO -- BTC: validation could be done per-field onChange, but this implementation mimics what V2 does
// Future iterations should do validation per-field onChange
export const UpdateProfileForm = () => {
  const dispatch = useDispatch();

  const userInfo = useCurrentUser();
  const sellerUsers = useSelector(Global.selectSellerUsers);
  const [showWeeklyDigestEmail] = useCountryConfig(CountryConfigNames.ShowWeeklyDigestEmail);

  const [fullName, setFullName] = useState(userInfo.name);
  const [email, setEmail] = useState(userInfo.email);
  const [shouldReceiveWeeklyDigest, setShouldReceiveWeeklyDigest] = useState(useWeeklyDigestSetting());
  const [profileErrorMap, setProfileErrorMap] = useState(getDefaultProfileErrorState);

  const resetProfileErrorMap = useCallback((input) => {
    const defaultErrorState = getDefaultProfileErrorState();
    setProfileErrorMap((prev) => ({
      ...prev,
      [input]: defaultErrorState[input],
    }));
  }, []);

  const handleFullNameChange = useCallback(
    (event) => {
      const { value } = event.target;
      setFullName(value);
      resetProfileErrorMap('fullName');
    },
    [resetProfileErrorMap],
  );

  const handleEmailChange = useCallback(
    (event) => {
      const { value } = event.target;
      setEmail(sanitizeEmailAddress(value));
      resetProfileErrorMap('email');
    },
    [resetProfileErrorMap],
  );

  const onSaveProfile = useCallback(async () => {
    const errorMap = validateProfile({ fullName, email });
    setProfileErrorMap(errorMap);
    const hasError = Object.keys(errorMap).reduce((acc, current) => acc.concat(errorMap[current]), []).length > 0;
    if (hasError) {
      return;
    }

    let result;
    try {
      result = await updateProfile(fullName, email, shouldReceiveWeeklyDigest);
    } catch (e) {
      LogService.logError(e);
      showErrorNotification(messages.changesFailed);
      return;
    }

    // dispatch actions
    dispatch(
      getCurrentUserSuccess({
        ...userInfo,
        name: result.name,
        email: result.email,
      }),
    ); // updates current user
    const updatedUsers = sellerUsers.map((user) => {
      if (user.userId !== userInfo.userId) {
        return user;
      }
      return {
        ...user,
        name: result.name,
        email: result.email,
      };
    });
    dispatch(updateUsersList(updatedUsers)); // updates users for seller
    dispatch(
      updateUserSetting({
        settingName: UserSettings.weeklyDigest.settingName,
        settingValue: result.shouldReceiveWeeklyDigest,
      }),
    );

    showSuccessNotification(messages.changesSaved);
  }, [fullName, email, shouldReceiveWeeklyDigest, userInfo, sellerUsers, dispatch]);

  // For fraud reduction, we need to prevent the
  // primary admin user from changing their email address.
  // Internal users (ShipStation employees) can change the
  // primary admin user's email address if needed.
  const sellerEmail = useSelector(Global.selectSellerEmail);
  const isSystemAdmin = useSelector(Global.selectUserisSysAdmin);
  const isPrimarySellerEmail = sellerEmail === userInfo.email;

  const shouldHideEmailTooltip = !isPrimarySellerEmail || isSystemAdmin;

  const hasFullNameError = profileErrorMap.fullName.length > 0;
  const hasEmailError = profileErrorMap.email.length > 0;

  return (
    <>
      <Text textType={TextType.H4} value={messages.updateProfileFormHeader} />
      <FlexColumn className={style.form}>
        <FlexColumn>
          <Text htmlFor='fullName' textType={TextType.Label} value={messages.fullName} />
          <Input
            className={style.input}
            data-testid='myprofile-fullname'
            hasError={hasFullNameError}
            id='myprofile-fullname'
            inlineMessage={hasFullNameError ? <FormattedMessage {...profileErrorMap.fullName[0]} /> : null}
            inlineMessageType={INLINE_MESSAGE_TYPES.error}
            name='fullName'
            onChange={handleFullNameChange}
            value={fullName}
            version={version2}
          />
        </FlexColumn>
        <FlexColumn>
          <Text htmlFor='fullName' textType={TextType.Label} value={messages.email} />
          <Tooltip shouldHideTooltip={shouldHideEmailTooltip}>
            <div>
              <Input
                className={style.input}
                data-testid='myprofile-email'
                disabled={isPrimarySellerEmail ? !isSystemAdmin : null}
                hasError={hasEmailError}
                id='myprofile-email'
                inlineMessage={hasEmailError ? <FormattedMessage {...profileErrorMap.email[0]} /> : null}
                inlineMessageType={INLINE_MESSAGE_TYPES.error}
                name='email'
                onChange={handleEmailChange}
                value={email}
                version={version2}
              />
            </div>
            <TooltipContent>
              <FormattedMessage {...messages.pleaseContactSupport} />
            </TooltipContent>
          </Tooltip>
        </FlexColumn>
        {showWeeklyDigestEmail ? (
          <Checkbox
            checked={shouldReceiveWeeklyDigest}
            className={style['form-checkbox']}
            label={<FormattedMessage {...messages.weeklyDigest} />}
            onChange={setShouldReceiveWeeklyDigest}
          />
        ) : null}
        <Button className={style['submit-button']} onClick={onSaveProfile} theme={THEME_CONSTANTS.secondary}>
          <FormattedMessage {...messages.updateProfileButton} />
        </Button>
      </FlexColumn>
    </>
  );
};
